
import { useEffect, useRef } from 'react';
import { useTheme } from '@/hooks/useTheme';

export function HackerBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const { theme } = useTheme();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let mouseX = -1000;
        let mouseY = -1000;

        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$#@%&*';
        const fontSize = 10; // Smaller font
        let columns = 0;
        let drops: number[] = [];

        // Grid for static characters
        let grid: string[] = [];

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            columns = Math.ceil(canvas.width / fontSize);
            drops = new Array(columns).fill(1);

            // Initialize grid with random characters
            const rows = Math.ceil(canvas.height / fontSize);
            grid = new Array(columns * rows).fill('').map(() =>
                characters.charAt(Math.floor(Math.random() * characters.length))
            );
        };

        const handleMouseMove = (e: MouseEvent) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
        };

        window.addEventListener('resize', resizeCanvas);
        window.addEventListener('mousemove', handleMouseMove);
        resizeCanvas();

        const draw = () => {
            // Semi-transparent black to create fade effect for drops
            // But for the flashlight effect, we want a dark background mostly
            ctx.fillStyle = theme === 'dark' ? '#020617' : '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const rows = Math.ceil(canvas.height / fontSize);

            // Draw the grid
            ctx.font = `${fontSize}px monospace`;
            ctx.textAlign = 'center';

            for (let i = 0; i < columns; i++) {
                for (let j = 0; j < rows; j++) {
                    const x = i * fontSize;
                    const y = j * fontSize;

                    // Calculate distance from mouse
                    const dx = x - mouseX;
                    const dy = y - mouseY;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const maxDistance = 300; // Flashlight radius

                    if (distance < maxDistance) {
                        // Calculate opacity based on distance, max 0.2 (20%)
                        const opacity = (1 - (distance / maxDistance)) * 0.2;

                        // Randomly change character occasionally
                        if (Math.random() > 0.98) {
                            const index = i * rows + j;
                            grid[index] = characters.charAt(Math.floor(Math.random() * characters.length));
                        }

                        const char = grid[i * rows + j];

                        // Color logic
                        if (theme === 'dark') {
                            // Blue matrix style for dark mode
                            if (Math.random() > 0.99) {
                                ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`; // Sparkle white
                            } else {
                                ctx.fillStyle = `rgba(0, 120, 255, ${opacity})`; // Blue
                            }
                        } else {
                            // Dark text for light mode
                            ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`;
                        }

                        ctx.fillText(char, x, y);
                    }
                }
            }

            animationFrameId = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(animationFrameId);
        };
    }, [theme]);

    return (
        <canvas
            ref={canvasRef}
            className="fixed top-0 left-0 w-full h-full -z-10 pointer-events-none"
        />
    );
}
